import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EnvelopeOpen, ChartBar, PaperPlaneTilt, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

function DeliveryBadge({ delivery, error }) {
  if (delivery === "logged") {
    return <Badge variant="outline">dry-run</Badge>;
  }
  if (error) {
    return <Badge variant="outline" className="border-[var(--vs-danger)]/40 text-[var(--vs-danger)]" title={error}>{delivery} (failed)</Badge>;
  }
  return <Badge variant="outline" className="border-[var(--vs-success)]/40 text-[var(--vs-success)]">{delivery}</Badge>;
}

function testButtonLabel(sending, channel, providers) {
  if (sending) return "Sending…";
  return providers[channel + "_live"] ? "Send (LIVE)" : "Send (dry-run)";
}

export default function NotificationsAdmin() {
  const [rows, setRows] = useState([]);
  const [providers, setProviders] = useState({ email: "logger", sms: "logger", email_live: false, sms_live: false });
  const [testOpen, setTestOpen] = useState(false);
  const [test, setTest] = useState({ channel: "email", to: "", subject: "VSMS test notification", body: "This is a VSMS test notification." });
  const [sending, setSending] = useState(false);

  const refresh = () => {
    api.get("/admin/notifications").then((r) => setRows(r.data || [])).catch(() => {});
    api.get("/admin/notifications/providers").then((r) => setProviders(r.data || {})).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  const byKind = rows.reduce((acc, r) => { acc[r.kind] = (acc[r.kind] || 0) + 1; return acc; }, {});

  const sendTest = async () => {
    setSending(true);
    try {
      const res = await api.post("/admin/notifications/test", test);
      const live = res.data?.delivery && res.data.delivery !== "logged";
      toast.success(live ? `Sent via ${res.data.delivery}` : "Logged (dry-run)");
      setTestOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <DashboardLayout title="Notifications" subtitle="Outbound emails + SMS. Paste API keys in backend/.env to flip from dry-run to LIVE.">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
        <div className="vs-card p-4">
          <div className="eyebrow">Total</div>
          <div className="font-display text-2xl font-semibold mt-1" data-testid="notif-stat-total">{rows.length}</div>
        </div>
        <div className="vs-card p-4">
          <div className="eyebrow flex items-center gap-1.5"><ChartBar size={11} /> Top kind</div>
          <div className="font-display text-base font-semibold mt-1">
            {Object.entries(byKind).sort((a, b) => b[1] - a[1])[0]?.[0] || "—"}
          </div>
        </div>
        <div className="vs-card p-4" data-testid="notif-provider-email">
          <div className="eyebrow">Email provider</div>
          <div className="font-display text-base font-semibold mt-1 flex items-center gap-1.5">
            {providers.email_live ? <CheckCircle size={14} weight="duotone" className="text-[var(--vs-success)]" /> : <Warning size={14} weight="duotone" className="text-[var(--vs-warning)]" />}
            {providers.email === "logger" ? "Dry-run" : providers.email}
          </div>
        </div>
        <div className="vs-card p-4" data-testid="notif-provider-sms">
          <div className="eyebrow">SMS provider</div>
          <div className="font-display text-base font-semibold mt-1 flex items-center gap-1.5">
            {providers.sms_live ? <CheckCircle size={14} weight="duotone" className="text-[var(--vs-success)]" /> : <Warning size={14} weight="duotone" className="text-[var(--vs-warning)]" />}
            {providers.sms === "logger" ? "Dry-run" : providers.sms}
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-[var(--vs-text-secondary)]">
          {providers.email_live || providers.sms_live ? "LIVE delivery is on for at least one channel." : "Both channels in dry-run — set RESEND_API_KEY / TWILIO_* to flip LIVE."}
        </div>
        <Dialog open={testOpen} onOpenChange={setTestOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="notif-send-test-btn"><PaperPlaneTilt size={14} className="mr-1.5" />Send test</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Send test notification</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Channel</Label>
                <Select value={test.channel} onValueChange={(v) => setTest({ ...test, channel: v })}>
                  <SelectTrigger data-testid="notif-test-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To</Label>
                <Input data-testid="notif-test-to" placeholder={test.channel === "email" ? "name@example.com" : "+919999999999"} value={test.to} onChange={(e) => setTest({ ...test, to: e.target.value })} />
              </div>
              {test.channel === "email" && (
                <div>
                  <Label>Subject</Label>
                  <Input data-testid="notif-test-subject" value={test.subject} onChange={(e) => setTest({ ...test, subject: e.target.value })} />
                </div>
              )}
              <div>
                <Label>Body</Label>
                <Input data-testid="notif-test-body" value={test.body} onChange={(e) => setTest({ ...test, body: e.target.value })} />
              </div>
              <Button disabled={sending || !test.to} onClick={sendTest} data-testid="notif-test-submit" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)] w-full">
                {testButtonLabel(sending, test.channel, providers)}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="vs-card overflow-x-auto" data-testid="notifications-table">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]">
            <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Kind</th>
              <th className="px-4 py-3">Channel</th>
              <th className="px-4 py-3">To</th>
              <th className="px-4 py-3">Subject</th>
              <th className="px-4 py-3">Delivery</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((n) => (
              <tr key={n.id} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 font-mono text-xs">{new Date(n.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className="border-[var(--vs-secondary)]/30 text-[var(--vs-secondary)]">{n.kind}</Badge>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{n.channel || "email"}</td>
                <td className="px-4 py-3 font-mono text-xs flex items-center gap-1.5"><EnvelopeOpen size={12} /> {n.to}</td>
                <td className="px-4 py-3 max-w-[420px] truncate" title={n.subject}>{n.subject || "—"}{n.has_attachments && <span className="ml-2 text-[10px] text-[var(--vs-secondary)]">[+pdf]</span>}</td>
                <td className="px-4 py-3 font-mono text-xs">
                  <DeliveryBadge delivery={n.delivery} error={n.delivery_error} />
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">No notifications recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
